import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../../redux/store';
import { setFrameFabricUrl, setBackCushionFabricUrl, setSeatFabricUrl, setArmrestFabricUrl } from '../../../redux/slices/customizationSlice';
import type { FabricZone } from '../customizerConfig';
import SwatchCard from '../shared/SwatchCard';
import { THEME } from '../theme';

interface FabricSectionProps {
  zones: FabricZone[];
}

const DISPATCH_MAP: Record<string, any> = {
  frameFabricUrl: setFrameFabricUrl,
  seatFabricUrl: setSeatFabricUrl,
  backCushionFabricUrl: setBackCushionFabricUrl,
  armrestFabricUrl: setArmrestFabricUrl,
};

const FabricSection: React.FC<FabricSectionProps> = ({ zones }) => {
  const dispatch = useDispatch();
  const swatches = useSelector((s: RootState) => s.customization.swatches);
  const customization = useSelector((s: RootState) => s.customization);
  const [activeZone, setActiveZone] = useState(zones[0]?.id || '');

  const currentZone = zones.find((z) => z.id === activeZone) || zones[0];
  const urls = currentZone ? (swatches[currentZone.swatchKey] || []) : [];
  const selected = currentZone ? ((customization as any)[currentZone.paramKey] as string | null) : null;
  const action = currentZone ? DISPATCH_MAP[currentZone.paramKey] : null;

  const totalSelected = useMemo(() => {
    return zones.filter((z) => (customization as any)[z.paramKey]).length;
  }, [zones, customization]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
      <div className="w-full mb-5">
        <div className="flex w-full">
          {zones.map((zone) => {
            const isActive = activeZone === zone.id;
            const hasSelection = !!(customization as any)[zone.paramKey];
            return (
              <button
                key={zone.id}
                onClick={() => setActiveZone(zone.id)}
                className="relative flex-1 pb-3 pt-1 text-[12px] font-semibold uppercase tracking-[0.12em] cursor-pointer transition-colors duration-200 text-center"
                style={{ color: isActive ? '#fff' : hasSelection ? THEME.primaryLight : 'rgba(255,255,255,0.3)' }}
              >
                {zone.label}
                {isActive && (
                  <motion.div
                    layoutId="zoneUnderline"
                    className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full"
                    style={{ background: THEME.primary, boxShadow: `0 0 10px ${THEME.primary}` }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
        <div className="h-px w-full" style={{ background: 'rgba(255,255,255,0.06)' }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeZone}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-[12px] font-bold" style={{ color: THEME.primaryLight }}>
              Available Fabrics
            </span>
            {totalSelected > 0 && (
              <span className="text-[10px] font-semibold" style={{ color: THEME.primaryLight }}>
                {totalSelected} Selected
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            {urls.length === 0 && (
              <div className="flex gap-3">
                {[1, 2, 3].map((i) => <div key={i} className="w-[88px] h-[88px] rounded-2xl bg-white/5 animate-pulse" />)}
              </div>
            )}
            {urls.map((url, i) => (
              <SwatchCard
                key={`${activeZone}-${i}`}
                url={url}
                label={`Swatch ${i + 1}`}
                selected={selected === url}
                onClick={() => action && dispatch(action(selected === url ? null : url))}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default FabricSection;
